/**
 * WegfahrerForm.js – Mitarbeiter-Formular (Phase 1)
 * 100% Mobile-First – optimiert für iPhone & iPad
 */
import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Fuel, Camera, CheckCircle2, Loader2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const WegfahrerForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(user?.location_ids?.[0] || '');
  const [pumpConfig, setPumpConfig] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
    pump_nr: '',
    fuel_type: '',
    liters: '',
    amount: '',
    employer_informed: false,
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const pricePerLiter = form.liters && form.amount
    ? (parseFloat(form.amount) / parseFloat(form.liters)).toFixed(3)
    : null;

  const availableFuels = form.pump_nr
    ? (pumpConfig.find(p => p.number === parseInt(form.pump_nr))?.fuels || [])
    : [];

  useEffect(() => {
    const token = localStorage.getItem('token');
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/api/admin/locations`, { headers: h })
      .then(r => {
        const allLocs = r.data || [];
        const myLocs = user?.location_ids?.length
          ? allLocs.filter(l => user.location_ids.includes(l.id))
          : allLocs;
        setLocations(myLocs);
        const initial = user?.location_ids?.[0] || (myLocs[0]?.id ?? '');
        if (initial) setSelectedLocation(initial);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/wegfahrer/config/pumps/${selectedLocation}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPumpConfig(r.data?.pumps || []))
      .catch(() => setPumpConfig([]));
  }, [selectedLocation]);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxW = 1200;
        const ratio = Math.min(maxW / img.width, maxW / img.height, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.75);
        setPhoto(compressed);
        setPhotoPreview(compressed);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pump_nr || !form.fuel_type || !form.liters || !form.amount) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/wegfahrer/cases`, {
        location_id: selectedLocation,
        incident_date: form.incident_date,
        incident_time: form.incident_time,
        pump_nr: parseInt(form.pump_nr),
        fuel_type: form.fuel_type,
        liters: parseFloat(form.liters),
        amount: parseFloat(form.amount.replace(',', '.')),
        receipt_photo_b64: photo,
        employer_informed: form.employer_informed,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(res.data.case_nr);
      toast.success(`Fall ${res.data.case_nr} erfolgreich gemeldet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(null);
    setForm({
      incident_date: new Date().toISOString().split('T')[0],
      incident_time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      pump_nr: '', fuel_type: '', liters: '', amount: '', employer_informed: false,
    });
    setPhoto(null);
    setPhotoPreview(null);
  };

  if (success) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-4 sm:p-6">
        <CheckCircle2 className="h-16 w-16 text-green-500" />
        <h2 className="text-xl sm:text-2xl font-bold text-center">Fall gemeldet!</h2>
        <p className="text-muted-foreground text-center text-sm sm:text-base">
          Fallnummer: <strong>{success}</strong>
        </p>
        <p className="text-xs sm:text-sm text-muted-foreground text-center">
          Admin und Stationsleitung wurden benachrichtigt.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Button onClick={resetForm} className="w-full sm:w-auto">Neuen Fall melden</Button>
          <Button variant="outline" onClick={() => navigate(-1)} className="w-full sm:w-auto">Zurück</Button>
        </div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto p-3 sm:p-4 md:p-6 space-y-4">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl shrink-0">
            <Fuel className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold">Wegfahrer melden</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">Schnelle Eingabe an der Kasse</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Standort – nur bei mehreren */}
          {locations.length > 1 && (
            <div className="space-y-1.5">
              <Label>Standort</Label>
              <select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border bg-background text-sm touch-manipulation">
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}

          {/* Datum + Uhrzeit */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Datum *</Label>
              <Input type="date" value={form.incident_date}
                onChange={e => setForm(f => ({...f, incident_date: e.target.value}))}
                className="h-11 text-base" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Uhrzeit *</Label>
              <Input type="time" value={form.incident_time}
                onChange={e => setForm(f => ({...f, incident_time: e.target.value}))}
                className="h-11 text-base" required />
            </div>
          </div>

          {/* Zapfsäule + Kraftstoff */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Zapfsäule *</Label>
              <select value={form.pump_nr}
                onChange={e => setForm(f => ({...f, pump_nr: e.target.value, fuel_type: ''}))}
                className="w-full h-11 px-3 rounded-xl border bg-background text-sm touch-manipulation" required>
                <option value="">Säule wählen</option>
                {pumpConfig.map(p => <option key={p.number} value={p.number}>Säule {p.number}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Kraftstoff *</Label>
              <select value={form.fuel_type}
                onChange={e => setForm(f => ({...f, fuel_type: e.target.value}))}
                className="w-full h-11 px-3 rounded-xl border bg-background text-sm touch-manipulation"
                required disabled={!form.pump_nr}>
                <option value="">Auswählen</option>
                {availableFuels.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>

          {/* Liter + Betrag */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Liter *</Label>
              <Input type="number" step="0.01" min="0" placeholder="55.31"
                value={form.liters} onChange={e => setForm(f => ({...f, liters: e.target.value}))}
                className="h-11 text-base" inputMode="decimal" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Betrag (€) *</Label>
              <Input type="number" step="0.01" min="0" placeholder="102.24"
                value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))}
                className="h-11 text-base" inputMode="decimal" required />
            </div>
          </div>

          {/* Literpreis */}
          {pricePerLiter && (
            <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl px-3 py-2.5">
              <span className="text-sm text-muted-foreground">Literpreis:</span>
              <span className="font-bold text-blue-700 dark:text-blue-300 text-base">{pricePerLiter} €/L</span>
            </div>
          )}

          {/* Foto */}
          <div className="space-y-2">
            <Label className="text-sm">Kassenbon / Beleg (Foto)</Label>
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Beleg"
                  className="w-full max-h-56 object-cover rounded-xl border" />
                <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full h-28 sm:h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center gap-2 hover:bg-muted/30 active:bg-muted/50 transition-colors touch-manipulation">
                <Camera className="h-7 w-7 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Foto aufnehmen oder hochladen</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
              className="hidden" onChange={handlePhoto} />
          </div>

          {/* Arbeitgeber informiert */}
          <label className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl cursor-pointer active:bg-amber-100 transition-colors touch-manipulation">
            <input type="checkbox" checked={form.employer_informed}
              onChange={e => setForm(f => ({...f, employer_informed: e.target.checked}))}
              className="h-5 w-5 mt-0.5 rounded border-amber-400 shrink-0"
              data-testid="employer-informed-checkbox" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Hast du deinen Arbeitgeber informiert?{' '}
                <span className="font-normal text-amber-600">(optional)</span>
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                Haken setzen wenn Admin / Stationsleitung bereits informiert wurde
              </p>
            </div>
          </label>

          {/* Submit */}
          <Button type="submit" disabled={loading}
            className="w-full h-13 text-base bg-red-600 hover:bg-red-700 gap-2 py-3.5 touch-manipulation">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Fuel className="h-5 w-5" />}
            Fall melden
          </Button>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default WegfahrerForm;
